/*
 * Copyright (c) 2024-2026.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import List from "toolbox/list";


let BaseIconList = {
    Container: "PiPackage", // LuContainer
    Node: "PiHardDrives",
    BuildServer: "PiCube",
    BuildSession: "PiCubeTransparent",
    Certificate: "PiCertificate",
    Image: "PiDisc",
    Volume: "PiHardDrive",
    Domain: "PiSignpost",
    Backup: "PiClockCounterClockwise",
    Deployment: "PiRocket",
    
    // Standard Icons
    Spinner: "CgSpinner",
    Edit: "PiPencil",
    Add: "IoAddOutline",
    Search: "IoSearchOutline",
    Check: "IoCheckmark",
    ArrowLeft: "PiCaretLeft",
    ArrowRight: "PiCaretRight",
    ArrowUp: "PiCaretUp",
    ArrowDown: "PiCaretDown",
    Drag: "GrDrag",
    Empty: "PiCactus",
    Copy: "PiClipboard",
    Error: "RiAlarmWarningLine",
    Warning: "PiWarning",
    Info: "RiInfoI",
    EditMode: "PiWrench",
    UserMode: "PiUser",
};


const Icons = function () {

    function getIconForFileExtension(fileExtension) {
        let iconName;
        // sometimes the file extensions are upper case
        fileExtension = fileExtension.toLowerCase()
        switch (fileExtension) {
            case '.docx': // Microsoft Word document
            case '.docm': // Microsoft Word macro-enabled document
            case '.dot': // Microsoft Word template before Word 2007
            case '.dotx': // Microsoft Word template
                iconName = "WordFile"
                break;
            case '.txt':
                iconName = "TextFile"
                break;

            case '.exe':
                iconName = "GrDocumentWindows"
                break;

            case '.zip':
                iconName = "ZipFile"
                break;

            case '.pdf':
                iconName = "PDFFile"
                break;


            case '.pptx':  // PowerPoint Presentation
            case '.pptm': // PowerPoint Macro-Enabled Presentation
            case '.ppt': // PowerPoint 97-2003 Presentation
                iconName = "PowerPointFile"
                break;

            case '.xlsx': // Excel Workbook
            case '.xlsm': // Excel Macro-Enabled Workbook (code)
            case '.xlsb': // Excel Binary Workbook
            case '.xls': // Excel 97- Excel 2003 Workbook
                iconName = "ExcelFile"
                break;

            case '.jpg': // JPEG image
            case '.jpeg': // JPEG image
            case '.png': // PNG image
            case '.gif': // GIF image
            case '.bmp': // Bitmap image
            case '.tiff': // TIFF image
            case '.webp': // WebP image
                iconName = "ImageFile";
                break;

            case '.mp3': // MP3 audio file
            case '.wav': // WAV audio file
            case '.flac': // FLAC audio file
            case '.aac': // AAC audio file
            case '.ogg': // OGG audio file
                iconName = "GrDocumentAudio";
                break;

            case '.mp4': // MP4 video file
            case '.mov': // QuickTime video file
            case '.avi': // AVI video file
            case '.mkv': // Matroska video file
            case '.webm': // WebM video file
                iconName = "AudioFile";
                break;

            case '.json': // JSON file
                iconName = "GrDocumentJson";
                break;

            case '.html': // HTML file
            case '.htm':  // HTML file
                iconName = "GrDocumentHtml";
                break;

            case '.css': // Cascading Style Sheets
                iconName = "GrDocumentCss";
                break;

            case '.csv': // CSV file (Comma-separated values)
                iconName = "GrDocumentCsv";
                break;

            case '.md': // Markdown file
                iconName = "GrDocumentMarkdown";
                break;

            case '.sqlite': // SQLite database file
                iconName = "GrDocumentDatabase";
                break;

            // New file formats added:

            case '.rar': // RAR archive
                iconName = "GrDocumentArchive";
                break;

            case '.tar': // TAR archive
                iconName = "GrDocumentArchive";
                break;

            case '.7z': // 7-Zip archive
                iconName = "GrDocumentArchive";
                break;

            case '.iso': // ISO disk image
                iconName = "GrDocumentIso";
                break;

            case '.apk': // Android Package
                iconName = "GrDocumentAndroid";
                break;

            case '.swf': // Flash file
                iconName = "GrDocumentFlash";
                break;

            case '.psd': // Adobe Photoshop Document
                iconName = "GrDocumentPhotoshop";
                break;

            case '.ai': // Adobe Illustrator file
                iconName = "GrDocumentIllustrator";
                break;

            case '.eps': // Encapsulated PostScript file
                iconName = "GrDocumentPostscript";
                break;

            case '.indd': // Adobe InDesign file
                iconName = "GrDocumentInDesign";
                break;

            case '.fla': // Adobe Flash Animation file
                iconName = "GrDocumentFlash";
                break;

            case '.otf': // OpenType font file
                iconName = "GrDocumentFont";
                break;

            case '.ttf': // TrueType font file
                iconName = "GrDocumentFont";
                break;

            case '.vcf': // vCard file
                iconName = "GrDocumentVcf";
                break;

            case '.svg': // Scalable Vector Graphics file
                iconName = "GrDocumentSvg";
                break;

            case '.log': // Log file
                iconName = "GrDocumentLog";
                break;

            case '.bak': // Backup file
                iconName = "GrDocumentBackup";
                break;

            case '.xml': // XML file
                iconName = "GrDocumentXml";
                break;

            default:
                iconName = "IoDocumentOutline"
                break;

        }
        return iconName

    }


    function getIconFoTwinName(twinName) {
        let iconMap = {
            // Customers
            "customers": "FiUser", // English
            "kunden": "FiUser", // German
            "clientes": "FiUser", // Spanish
            "clients": "FiUser", // French
            // Customers
            "employee": "FiUser", // English
            "mitarbeiter": "FiUser", // German
            "empleada": "FiUser", // Spanish
            "employé": "FiUser", // French
            // Tasks
            "tasks": "BiTask", // English
            "aufgaben": "BiTask", // German
            "tareas": "BiTask", // Spanish
            "tâches": "BiTask", // French
            // Cars
            "cars": "IoCarSportOutline", // English
            "autos": "IoCarSportOutline", // German
            "fahrzeug": "IoCarSportOutline", // German
            "carros": "IoCarSportOutline", // Spanish
            "voitures": "IoCarSportOutline", // French
            // Plane
            "plane": "IoAirplaneOutline", // English
            "flugzeug": "IoAirplaneOutline", // German
            "avión": "IoAirplaneOutline", // Spanish
            "avion": "IoAirplaneOutline", // French
            // House
            "house": "BsHouses", // English
            "home": "BsHouses", // English
            "haus": "BsHouses", // German
            "casa": "BsHouses", // Spanish
            "maison": "BsHouses", // French
            // Friends
            "friends": "GiThreeFriends", // English
            "freunde": "GiThreeFriends", // German
            "boys": "GiThreeFriends", // German
            "amigas": "GiThreeFriends", // Spanish
            "amigos": "GiThreeFriends", // Spanish
            "amies": "GiThreeFriends", // French
            "amis": "GiThreeFriends", // French
            // Test
            "test": "LuTestTubeDiagonal", // English + German +French
            "prueba": "LuTestTubeDiagonal", // Spanish
            // Project
            "project": "IoFlagOutline", // English
            "projekt": "IoFlagOutline", // German
            "proyecto": "IoFlagOutline", // Spanish
            "projet": "IoFlagOutline", // French
            // Template
            "template": "LuBookTemplate", // English
            "vorlage": "LuBookTemplate", // German
            "plantilla": "LuBookTemplate", // Spanish
            "modèle": "LuBookTemplate", // French
            // Email
            "email": "MdOutlineEmail", // English
            "e-mail": "MdOutlineEmail", // English
            // Project
            "invoice": "PiReceipt", // English
            "rechnung": "PiReceipt", // German
            "factura": "PiReceipt", // Spanish
            "facture": "PiReceipt", // French
            // Orders
            "orders": "FiShoppingCart", // English
            "bestellungen": "FiShoppingCart", // German
            "pedidos": "FiShoppingCart", // Spanish
            "commandes": "FiShoppingCart", // French
            // Products
            "products": "BiBox", // English
            "produkte": "BiBox", // German
            "productos": "BiBox", // Spanish
            "produits": "BiBox", // French
            // Dashboard
            "dashboard": "RiDashboardFill", // English
            "instrumententafel": "RiDashboardFill", // German
            "tablero": "RiDashboardFill", // Spanish
            "tableau": "RiDashboardFill", // French
            // Settings
            "settings": "FiSettings", // English
            "einstellungen": "FiSettings", // German
            "configuraciones": "FiSettings", // Spanish
            "paramètres": "FiSettings", // French
            // Reports
            "reports": "BiChart", // English
            "berichte": "BiChart", // German
            "informes": "BiChart", // Spanish
            "rapports": "BiChart", // French
            // Support
            "support": "BiSupport", // English
            "unterstützung": "BiSupport", // German
            "soporte": "BiSupport", // Spanish
            "soutien": "BiSupport", // French
            // Notifications
            "notifications": "RiNotification3Line", // English
            "benachrichtigungen": "RiNotification3Line", // German
            "notificaciones": "RiNotification3Line", // Spanish
            // Leads
            "leads": "PiUserCircleDashed", // English
            "interessenten": "PiUserCircleDashed", // German
            "prospectos": "PiUserCircleDashed", // Spanish
            "prospects": "PiUserCircleDashed", // French
            // Tickets
            "tickets": "PiTicket", // English
            "supportanfragen": "PiTicket", // German
            "tickets_soporte": "PiTicket", // Spanish
            "tickets_support": "PiTicket", // French

        }


        return List.matchList(twinName, iconMap)

    }

    function processNameToIcon(processName) {
        let iconMap = {
            // create
            "new": "IoIosAdd", // English
            "create": "IoIosAdd", // English
            "neues": "IoIosAdd", // German
            "neue": "IoIosAdd", // German
            "neuer": "IoIosAdd", // German
            "anlegen": "IoIosAdd", // German
            "erstellen": "IoIosAdd", // German
            "crear": "IoIosAdd", // Spanish
            "créer": "IoIosAdd", // French
            // delete
            "delete": "IoTrashOutline", // English
            "löschen": "IoTrashOutline", // German
            "borrar": "IoTrashOutline", // Spanish
            "supprimer": "IoTrashOutline", // French
            // file formats
            "excel": "ExcelFile",
            "word": "WordFile",
            "access": "AccessFile",
            "csv": "BsFiletypeCsv",
            "pdf": "PDFFile", // calculate
            "calculate": "IoCalculatorOutline", // English
            "berechnen": "IoCalculatorOutline", // German
            "berechnung": "IoCalculatorOutline", // German
            "ermitteln": "IoCalculatorOutline", // German
            "calcular": "IoCalculatorOutline", // Spanish
            "calculer": "IoCalculatorOutline", // French
            // clone / copy
            "copy": "IoCopyOutline", // English
            "clone": "IoCopyOutline", // English
            "duplizieren": "IoCopyOutline", // German
            "kopieren": "IoCopyOutline", // German
            "duplicar": "IoCopyOutline", // Spanish
            "dupliquer": "IoCopyOutline", // French
            // Test
            "test": "LuTestTube", // English + German +French
            "prueba": "LuTestTube", // Spanish
            // Demo
            "demo": "PiPresentation", // English + German
            // save
            "save": "IoIosSave", // English
            "speichern": "IoIosSave", // German
            "guardar": "IoIosSave", // Spanish
            "sauvegarder": "IoIosSave", // French
            // uploade
            "upload": "IoCloudUpload", // English
            "hochladen": "IoCloudUpload", // German
            "subir": "IoCloudUpload", // Spanish
            // download
            "download": "IoCloudDownload", // English
            "herunterladen": "IoCloudDownload", // German
            "descargar": "IoCloudDownload", // Spanish
            // search
            "search": "IoSearchOutline", // English
            "suchen": "IoSearchOutline", // German
            "buscar": "IoSearchOutline", // Spanish
            // filter
            "filter": "IoFunnelOutline", // German
            "filtrar": "IoFunnelOutline", // Spanish
            "filtrer": "IoFunnelOutline", // French
            // open
            "open": "PiArrowCircleUpRight", // English
            "öffnen": "PiArrowCircleUpRight", // German
            "abierto": "PiArrowCircleUpRight", // Spanish
            "ouvrir": "PiArrowCircleUpRight", // French
        }


        return List.matchList(processName, iconMap)

    }

    return {getIconForFileExtension, getIconFoTwinName, processNameToIcon}

}()

export {Icons, BaseIconList}